/**
 * Product Showcase Grid
 * Handles mobile Swiper initialization with peek-through effect
 */

(function() {
  'use strict';

  // Global storage for Swiper instances
  const swiperInstances = new Map();

  /**
   * ProductShowcaseGrid Class
   */
  class ProductShowcaseGrid {
    constructor(sectionElement) {
      this.section = sectionElement;
      this.sectionId = sectionElement.getAttribute('data-section-id');

      // Elements
      this.mobileSwipersContainer = this.section.querySelector('[data-mobile-swipers]');
      this.swiperTop = this.section.querySelector('[data-swiper-row="top"]');
      this.swiperBottom = this.section.querySelector('[data-swiper-row="bottom"]');

      // Initialize
      this.init();
    }

    /**
     * Initialize the component
     */
    init() {
      if (!this.section) return;

      // Only initialize Swipers on mobile
      if (this.isMobile()) {
        this.initMobileSwipers();
      }

      // Reinitialize on resize
      this.bindResizeListener();
    }

    /**
     * Check if current viewport is mobile
     */
    isMobile() {
      return window.innerWidth <= 768;
    }

    /**
     * Initialize mobile Swipers with peek-through effect
     */
    initMobileSwipers() {
      if (!this.mobileSwipersContainer) return;

      console.log('[Product Showcase] Initializing mobile Swipers...');

      // Initialize top Swiper
      if (this.swiperTop) {
        this.initSwiper(this.swiperTop, 'top');
      }

      // Initialize bottom Swiper
      if (this.swiperBottom) {
        this.initSwiper(this.swiperBottom, 'bottom');
      }
    }

    /**
     * Initialize a single Swiper instance
     * @param {HTMLElement} element - Swiper container element
     * @param {String} position - 'top' or 'bottom'
     */
    initSwiper(element, position) {
      const swiperKey = this.sectionId + '-swiper-' + position;

      // Destroy existing instance if present
      if (swiperInstances.has(swiperKey)) {
        console.log(`[Product Showcase] Destroying existing Swiper: ${swiperKey}`);
        swiperInstances.get(swiperKey).destroy();
        swiperInstances.delete(swiperKey);
      }

      // Check if element has slides
      const slides = element.querySelectorAll('.swiper-slide');
      if (slides.length === 0) {
        console.warn(`[Product Showcase] No slides found for ${position} Swiper`);
        return;
      }

      console.log(`[Product Showcase] Initializing ${position} Swiper with ${slides.length} slides`);

      // Swiper configuration
      const config = {
        slidesPerView: 1.1,
        spaceBetween: 16,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        centeredSlides: false,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,

        // Enable touch/mouse dragging
        simulateTouch: true,
        allowTouchMove: true,
        touchRatio: 1,
        resistance: true,
        resistanceRatio: 0.85,

        // Fix for dragging in gaps between slides
        touchEventsTarget: 'container',

        // Enable slide tracking for peek-through effect
        watchSlidesVisibility: true,

        // Smooth transitions
        speed: 600,

        // Enable GPU acceleration
        cssMode: false,

        // Event callbacks for peek-through effect
        on: {
          init: function() {
            console.log(`[Product Showcase] ${position} Swiper initialized`);
            updateCardVisibility(this);
          },
          slideChange: function() {
            updateCardVisibility(this);
          },
          setTranslate: function() {
            updateCardVisibility(this);
          },
          resize: function() {
            updateCardVisibility(this);
          }
        }
      };

      /**
       * Update card visibility based on slide progress
       * Creates smooth peek-through effect
       */
      function updateCardVisibility(swiper) {
        if (!swiper || !swiper.slides) return;

        const activeIndex = swiper.activeIndex;
        const slidesPerView = swiper.params.slidesPerView;
        const fullyVisibleSlides = Math.floor(slidesPerView);

        console.log(`[Peek-through ${position}] Active: ${activeIndex}, PerView: ${slidesPerView}`);

        swiper.slides.forEach((slide, index) => {
          const card = slide.querySelector('.product-card-grid');
          if (!card) return;

          // Calculate if this slide is in the main visible area
          const isFullyVisible = index >= activeIndex && index < activeIndex + fullyVisibleSlides;

          // Get slide progress for fine-tuning
          const progress = slide.progress || 0;

          let opacity = 1;
          let scale = 1;

          // Only apply fade to slides outside the main visible area
          if (!isFullyVisible) {
            // Check if this is a peek-through slide (partially visible)
            const isPeekThrough = (index === activeIndex - 1) ||
                                  (index === activeIndex + fullyVisibleSlides);

            if (isPeekThrough) {
              // Peek-through slides: moderate fade
              opacity = 0.6;
              scale = 0.98;
            } else {
              // Completely outside: strong fade
              opacity = 0.3;
              scale = 0.96;
            }
          }

          // Apply styles with smooth transitions
          card.style.opacity = opacity.toFixed(2);
          card.style.transform = `scale(${scale.toFixed(3)})`;
        });
      }

      // Initialize Swiper
      const swiperInstance = new Swiper(element, config);
      swiperInstances.set(swiperKey, swiperInstance);

      console.log(`[Product Showcase] ${position} Swiper instance created and stored`);
    }

    /**
     * Destroy all Swiper instances
     */
    destroySwipers() {
      const topKey = this.sectionId + '-swiper-top';
      const bottomKey = this.sectionId + '-swiper-bottom';

      if (swiperInstances.has(topKey)) {
        swiperInstances.get(topKey).destroy();
        swiperInstances.delete(topKey);
        console.log('[Product Showcase] Destroyed top Swiper');
      }

      if (swiperInstances.has(bottomKey)) {
        swiperInstances.get(bottomKey).destroy();
        swiperInstances.delete(bottomKey);
        console.log('[Product Showcase] Destroyed bottom Swiper');
      }
    }

    /**
     * Bind resize listener for responsive behavior
     */
    bindResizeListener() {
      let resizeTimer;

      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const isMobileNow = this.isMobile();
          const hasActiveSwipers = swiperInstances.has(this.sectionId + '-swiper-top') ||
                                    swiperInstances.has(this.sectionId + '-swiper-bottom');

          // Initialize Swipers if we're now on mobile and don't have active Swipers
          if (isMobileNow && !hasActiveSwipers) {
            console.log('[Product Showcase] Resize to mobile - initializing Swipers');
            this.initMobileSwipers();
          }
          // Destroy Swipers if we're now on desktop and have active Swipers
          else if (!isMobileNow && hasActiveSwipers) {
            console.log('[Product Showcase] Resize to desktop - destroying Swipers');
            this.destroySwipers();
          }
        }, 250);
      });
    }

    /**
     * Destroy all instances and cleanup
     */
    destroy() {
      this.destroySwipers();
    }
  }

  /**
   * Initialize section
   * @param {HTMLElement} section - Section element
   */
  function initSection(section) {
    if (!section) return;

    const sectionId = section.getAttribute('data-section-id');
    console.log(`[Product Showcase] Initializing section: ${sectionId}`);

    // Store instance for potential cleanup
    const instance = new ProductShowcaseGrid(section);

    // Store reference for Shopify theme editor
    section._productShowcaseGrid = instance;
  }

  /**
   * Shopify Theme Editor: Section Load Event
   */
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('[data-section-type="product-showcase-grid"]');
    if (section) {
      console.log('[Product Showcase] Theme editor: section load');
      // Destroy previous instance if exists
      if (section._productShowcaseGrid) {
        section._productShowcaseGrid.destroy();
      }
      // Initialize new instance
      initSection(section);
    }
  });

  /**
   * Shopify Theme Editor: Section Unload Event
   */
  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('[data-section-type="product-showcase-grid"]');
    if (section && section._productShowcaseGrid) {
      console.log('[Product Showcase] Theme editor: section unload');
      section._productShowcaseGrid.destroy();
    }
  });

  /**
   * Initialize on DOM ready
   */
  function initOnReady() {
    const sections = document.querySelectorAll('[data-section-type="product-showcase-grid"]');
    console.log(`[Product Showcase] Found ${sections.length} section(s) on page`);
    sections.forEach(initSection);
  }

  // Check if DOM is already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnReady);
  } else {
    initOnReady();
  }

  /**
   * Intersection Observer for lazy initialization
   * Only initialize Swiper when section is near viewport
   */
  if ('IntersectionObserver' in window) {
    const observerOptions = {
      root: null,
      rootMargin: '200px',
      threshold: 0
    };

    const sectionObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !entry.target._productShowcaseGrid) {
          console.log('[Product Showcase] Section entering viewport - initializing');
          initSection(entry.target);
          sectionObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe sections on page load
    document.addEventListener('DOMContentLoaded', function() {
      const sections = document.querySelectorAll('[data-section-type="product-showcase-grid"]');
      sections.forEach(function(section) {
        sectionObserver.observe(section);
      });
    });
  }

})();
